import { useState, useEffect, useMemo } from "react";
import { collection, getDocs, updateDoc, doc, addDoc, query, orderBy, deleteDoc} from "firebase/firestore";
import { db, auth } from "./firebaseConfig";
import { createUserWithEmailAndPassword } from "firebase/auth";
import "./index.css";

const DEFAULT_PHOTO =
  "https://firebasestorage.googleapis.com/v0/b/spp-app-05082025.firebasestorage.app/o/spp-app-admin-uploads-05082025%2FIcon%20Perfil%20Verde.png?alt=media&token=cfa50eab-f365-400d-9a23-bc0e561b2a4b";

function Utilizadores() {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "utilizadores"), orderBy("created_time", "desc"));
      const snapshot = await getDocs(q);
      setUsers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filterUsers = () => {
    return users.filter((user) => {
      const nameEmailMatch =
        (user.display_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.email || "").toLowerCase().includes(searchQuery.toLowerCase());

      let statusMatch = true;
      if (statusFilter === "approved") statusMatch = user.status_aprovado === true;
      if (statusFilter === "pending") statusMatch = !user.status_aprovado;

      return nameEmailMatch && statusMatch;
    });
  };

  const filteredUsers = useMemo(filterUsers, [users, searchQuery, statusFilter]);

  const toggleMember = async (id, currentValue) => {
    const docRef = doc(db, "utilizadores", id);
    await updateDoc(docRef, { socioSPP: !currentValue });
    await fetchUsers();
  };

  const toggleApproved = async (id, currentValue) => {
    const docRef = doc(db, "utilizadores", id);
    await updateDoc(docRef, { status_aprovado: !currentValue });
    await fetchUsers();
  };

  const approveUser = async (id) => toggleApproved(id, false);
  const revertApproval = async (id) => toggleApproved(id, true);

  const deleteUser = async (id) => {
    if (!window.confirm("Tem certeza de que deseja excluir este utilizador da app?")) return;

    const userToDelete = users.find((u) => u.id === id);
    if (!userToDelete || !userToDelete.uid) {
      alert("User UID not found.");
      return;
    }

    const uid = userToDelete.uid;

    try {
      const res = await fetch(
        "https://us-central1-spp-app-05082025.cloudfunctions.net/apagarUtilizadorAuth",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uid }),
        }
      );

      if (!res.ok) {
        const errorText = await res.text();
        alert("Erro a apagar o utilizador: " + res.status);
        return;
      }

      const data = await res.json();

      await deleteDoc(doc(db, "utilizadores", userToDelete.id));
      await fetchUsers();
      alert("Utilizador removido com sucesso!");
    } catch (err) {
      alert("Erro a apagar o utilizador: " + err.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const exportCSV = () => {
    if (!filteredUsers.length) return;
    const headers = ["Name", "Email", "Registration Date", "SPP Member", "Approved", "Consent"];
    const rows = filteredUsers.map((u) => [
      u.display_name || "",
      u.email || "",
      u.created_time ? formatDate(u.created_time) : "—",
      u.socioSPP ? "Yes" : "No",
      u.status_aprovado ? "Yes" : "No",
      u.consentimento ? "Yes" : "No",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const bom = "\uFEFF";
    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "users.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const importCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const text = await file.text();
    const rows = text.split("\n").slice(1);

    for (let row of rows) {
      if (!row.trim()) continue;
      const [display_name, email, password, socioSPP, consent] = row
        .split(",")
        .map((v) => v.replace(/"/g, "").trim());
      if (!email || !password) continue;

      try {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        const user = cred.user;

        await addDoc(collection(db, "utilizadores"), {
          uid: user.uid,
          display_name,
          email,
          socioSPP: socioSPP === "Yes",
          consentimento: consent === "Yes",
          photo_url: DEFAULT_PHOTO,
          status_aprovado: false,
          created_time: new Date(),
        });
      } catch (err) {
        console.error(`Erro a importar o utilizador: ${email}:`, err);
      }
    }

    setImporting(false);
    await fetchUsers();
    alert("Importação concluída com sucesso!");
  };

  return (
    <div className="table-container">
      <div>
        <input
          type="text"
          className="input"
          placeholder="Procurar por nome ou email"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Todos os status</option>
          <option value="approved">Aprovado</option>
          <option value="pending">Pendente</option>
        </select>
        <button className="btn btn-other" onClick={exportCSV}>
          Exportar CSV
        </button>
        <label className="btn btn-other">
          Importar CSV
          <input type="file" accept=".csv" style={{ display: "none" }} onChange={importCSV} />
        </label>
      </div>

      <div style={{ margin: "12px 0", fontWeight: "600" }}>
        Total de utilizadores: {filteredUsers.length}
      </div>

      {loading ? (
        <p style={{ textAlign: "center", color: "#292f2c" }}>A carregar utilizadores ...</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Foto</th>
              <th>Nome</th>
              <th>Email</th>
              <th>Inscrição</th>
              <th>Sócio SPP</th>
              <th>Status Aprovação</th>
              <th>Consentimento</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id}>
                <td>
                  <img
                    src={u.photo_url || DEFAULT_PHOTO}
                    alt={u.display_name || "Photo"}
                    style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover" }}
                  />
                </td>
                <td>{u.display_name || "—"}</td>
                <td>{u.email || "—"}</td>
                <td>{u.created_time ? formatDate(u.created_time) : "—"}</td>
                <td>
                  <input type="checkbox" checked={u.socioSPP || false} onChange={() => toggleMember(u.id, u.socioSPP)} />
                </td>
                <td>
                  <input type="checkbox" checked={u.status_aprovado || false} onChange={() => toggleApproved(u.id, u.status_aprovado)} />
                </td>
                <td>{u.consentimento ? "Sim" : "Não"}</td>
                <td>
                  {u.status_aprovado ? (
                    <button className="btn btn-deactive" onClick={() => revertApproval(u.id)}>
                      Desativar
                    </button>
                  ) : (
                    <button className="btn btn-approve" onClick={() => approveUser(u.id)}>
                      Aprovar
                    </button>
                  )}
                  <button className="btn btn-delete" onClick={() => deleteUser(u.id)}>
                    Apagar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default Utilizadores;
